/**
 * Cell renderers for {@link ./LinesTable.tsx}.
 *
 * Split out purely to keep that file under the line budget - every rule here
 * is still read as part of the table, just from its own file.
 */

import { Badge } from "@bruntworkwear-apps/ui";
import { money, rate } from "@/shared/lib";
import type { CostedLine } from "@/shared/api";
import styles from "./LinesTable.module.css";

/** True when the tracker owed this line a freight rate and carries none. */
function isAwaitingFreight(line: CostedLine): boolean {
  return line.landing_rate_source === "awaiting_freight";
}

/** True when the line was landed at the freight actually invoiced for its shipment. */
function isActualFreight(line: CostedLine): boolean {
  return line.landing_rate_source === "actual_freight";
}

/**
 * True when this line's landing rate is a Boot Barn wholesale quote taken for
 * want of an invoice - still a gap to chase, exactly like an awaiting-freight
 * line, just for a different reason.
 */
function isBbWcEstimate(line: CostedLine): boolean {
  return line.landing_rate_source === "bb_wc_estimate";
}

/**
 * True when this line's landing rate is the Boot Barn wholesale quote applied
 * over an invoiced figure - a deliberate override, not a gap.
 */
function isBbWcRate(line: CostedLine): boolean {
  return line.landing_rate_source === "bb_wc_rate";
}

/**
 * True when this line's customs duty is a Boot Barn wholesale quote taken for
 * want of an invoice.
 */
function isBbWcCustomsEstimate(line: CostedLine): boolean {
  return line.customs_rate_source === "bb_wc_customs_estimate";
}

/**
 * True when this line's customs duty is the Boot Barn wholesale quote applied
 * over an invoiced figure.
 */
function isBbWcCustomsRate(line: CostedLine): boolean {
  return line.customs_rate_source === "bb_wc_customs_rate";
}

/**
 * Why the tracker was expected to price this line, for the awaiting-freight
 * tooltip. The two cases are chased with different people and different columns,
 * so "ask Ops" on its own is not enough to act on.
 */
function awaitingReason(line: CostedLine): string {
  return line.is_domestic
    ? "No USA freight rate on the tracker - ask Ops"
    : "This PO ships at actual freight, but the shipment invoice is blank - ask Ops";
}

/**
 * Renders the landing rate, saying so where it is a fact rather than a quote.
 *
 * The rate band column already reads "actual freight", but the number itself is
 * what gets checked against the tracker, so the explanation belongs on it too.
 */
export function landingRateCell(line: CostedLine): React.ReactNode {
  if (isActualFreight(line)) {
    return (
      <span title="The freight actually invoiced for this shipment, from the tracker">
        {rate(line.landing_rate)}
      </span>
    );
  }
  return rate(line.landing_rate);
}

/**
 * Renders landing cost, marking the lines held at zero pending a freight rate.
 *
 * A plain 0.00 is indistinguishable from a genuine nil cost, which is exactly
 * the confusion the blue fill exists to prevent on the export.
 *
 * A Boot Barn wholesale quote taken for want of an invoice gets the same blue
 * fill: it is still a gap to chase, just a different one from the tracker
 * carrying no rate at all. A quote applied *over* an invoiced figure is the
 * opposite case - a deliberate override, not a gap - so it gets no fill, only
 * the badge naming the figure it superseded.
 */
export function landingCell(line: CostedLine): React.ReactNode {
  if (isAwaitingFreight(line)) {
    return (
      <span className={styles.awaiting} title={awaitingReason(line)}>
        {money(line.landing_cost)}
      </span>
    );
  }

  if (isBbWcEstimate(line)) {
    return (
      <span
        className={styles.awaiting}
        title="Boot Barn wholesale quote - the forwarder has not invoiced freight on this shipment yet"
      >
        <Badge tone="brand">BB WC quote</Badge> {money(line.landing_cost)}
      </span>
    );
  }

  if (isBbWcRate(line)) {
    return (
      <span
        title={`Boot Barn wholesale quote applied over the invoiced ${rate(
          line.landing_rate_superseded,
        )}`}
      >
        <Badge tone="warning">BB WC rate</Badge> {money(line.landing_cost)}
      </span>
    );
  }

  return money(line.landing_cost);
}

/** Renders customs as the green USA marker for a domestic line, matching the export. */
export function customsCell(line: CostedLine, value: number | null): React.ReactNode {
  if (line.is_domestic) return <span className={styles.domestic}>USA</span>;
  return value === null ? "—" : money(value);
}

/**
 * Renders the customs total, marking a Boot Barn wholesale duty quote the same
 * way {@link landingCell} marks a landing one: a quote taken for want of an
 * invoice is still a gap to chase and gets the blue fill; a quote applied over
 * an invoiced figure is a deliberate override and gets only the badge naming
 * what it superseded.
 *
 * Split from {@link customsCell} rather than folded into it: the rate column
 * reuses that plainer renderer unchanged, and only the total column - the
 * figure actually booked - needs to say where a Boot Barn wholesale duty came
 * from.
 */
export function customsTotalCell(line: CostedLine): React.ReactNode {
  if (line.is_domestic) return <span className={styles.domestic}>USA</span>;

  if (isBbWcCustomsEstimate(line)) {
    return (
      <span
        className={styles.awaiting}
        title="Boot Barn wholesale quote - the forwarder has not invoiced customs on this shipment yet"
      >
        <Badge tone="brand">BB WC quote</Badge> {money(line.customs_total)}
      </span>
    );
  }

  if (isBbWcCustomsRate(line)) {
    return (
      <span
        title={`Boot Barn wholesale quote applied over the invoiced ${rate(
          line.customs_rate_superseded,
        )} duty`}
      >
        <Badge tone="warning">BB WC rate</Badge> {money(line.customs_total)}
      </span>
    );
  }

  return line.customs_total === null ? "—" : money(line.customs_total);
}

/**
 * Where this line's customs rate came from.
 *
 * A line priced from its own product says which one, because that is the fact a
 * reviewer checks against the tracker - and on an order covering two boots at
 * two rates it is the only thing distinguishing two otherwise identical rows.
 * "averaged" is now reserved for a line that genuinely took a blend.
 */
export function sourceCell(line: CostedLine): React.ReactNode {
  if (line.is_domestic) {
    return (
      <Badge tone={isAwaitingFreight(line) ? "brand" : "success"}>
        {isAwaitingFreight(line) ? "ask Ops" : "domestic"}
      </Badge>
    );
  }

  // Customs first. Freight and customs are independent, and a line can be
  // missing both - a brand-new shipment routinely is. "no rate" is the stronger
  // statement of the two, since it means the line cannot be costed at all.
  if (line.customs_rate_source === "unavailable") return <Badge tone="danger">no rate</Badge>;

  // Then freight. Checked before the customs sources below because an import
  // held at zero for want of a shipment invoice would otherwise render as an
  // ordinary matched line, which is the one thing the reviewer must not read
  // past. That does not apply above: "no rate" already stops them.
  if (isAwaitingFreight(line)) return <Badge tone="brand">ask Ops</Badge>;

  if (line.customs_rate_product) {
    return (
      <span title={`Rated from ${line.customs_rate_product} on the tracker`}>
        <Badge tone={line.customs_rate_source === "weighted_average" ? "warning" : "neutral"}>
          {line.customs_rate_product}
        </Badge>
      </span>
    );
  }

  return line.customs_rate_source === "weighted_average" ? (
    <Badge tone="warning">averaged</Badge>
  ) : (
    <Badge tone="neutral">matched</Badge>
  );
}
